import React, { useState, useMemo } from 'react';
import moment from 'moment';
import cn from 'classnames';

import { useShareApprovalsQuery } from 'src/api/queries/shareQueries';
import { ShareApproval, ApprovalStatus, ShareAsset } from 'src/types/TShare';
import Shimmer from 'src/components/Shimmer';
import Dropdown from 'src/components/Dropdown';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { getThumbnailUrl, getODIThumbnailUrl } from 'src/utils/previews';
import { Checks, X, ArrowsClockwise } from 'src/common/icons';

import styles from './ShareApprovalsPanel.module.scss';

interface ShareApprovalsPanelProps {
  shareId: string;
  assets?: ShareAsset[];
}

type FilterStatus = 'all' | ApprovalStatus;

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
];

export const ShareApprovalsPanel: React.FC<ShareApprovalsPanelProps> = ({ shareId, assets = [] }) => {
  const accessToken = useAccessToken();
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');

  const { data: approvalsResult, isLoading } = useShareApprovalsQuery(shareId);

  // Calculate stats from assets (which have approval_status field)
  const stats = useMemo(() => {
    return {
      total: assets.length,
      pending: assets.filter((a) => !a.approval_status || a.approval_status === 'PENDING').length,
      approved: assets.filter((a) => a.approval_status === 'APPROVED').length,
      rejected: assets.filter((a) => a.approval_status === 'REJECTED').length,
    };
  }, [assets]);

  // Combine asset info with approval details for display
  const displayItems = useMemo(() => {
    const approvalDetails = approvalsResult?.data?.approvals || [];
    
    // Create a map of asset_id to approval details
    const approvalMap = new Map<string, ShareApproval>();
    approvalDetails.forEach((approval) => {
      approvalMap.set(approval.asset_id, approval);
    });

    // Create display items from assets
    const items = assets.map((asset) => {
      const approvalDetail = approvalMap.get(asset.id);
      return {
        asset_id: asset.id,
        asset_name: asset.name,
        content_type: asset.content_type,
        status: (asset.approval_status || 'PENDING') as ApprovalStatus,
        reviewer_name: approvalDetail?.reviewer_name,
        reviewer_email: approvalDetail?.reviewer_email,
        notes: approvalDetail?.notes,
        updated_utc: approvalDetail?.updated_utc,
        shared_links: asset.shared_links,
      };
    });

    // Filter by status if needed
    if (statusFilter === 'all') return items;
    return items.filter((item) => item.status === statusFilter);
  }, [assets, approvalsResult, statusFilter]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return moment(dateString).format('MMM D, YYYY [at] h:mm A');
  };

  const getStatusIcon = (status: ApprovalStatus) => {
    switch (status) {
      case 'APPROVED':
        return <Checks className={cn(styles.statusIcon, styles.approved)} />;
      case 'REJECTED':
        return <X className={cn(styles.statusIcon, styles.rejected)} />;
      case 'PENDING':
      default:
        return <ArrowsClockwise className={cn(styles.statusIcon, styles.pending)} />;
    }
  };

  const getStatusClass = (status: ApprovalStatus) => {
    switch (status) {
      case 'APPROVED':
        return styles.approved;
      case 'REJECTED':
        return styles.rejected;
      case 'PENDING':
      default:
        return styles.pending;
    }
  };

  if (isLoading && assets.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.loadingState}>
          <Shimmer w="100%" h="60px" radius="8px" />
          <Shimmer w="100%" h="100px" radius="8px" />
          <Shimmer w="100%" h="100px" radius="8px" />
        </div>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyState}>
          <Checks className={styles.emptyIcon} />
          <h3>No assets to review</h3>
          <p>Assets requiring approval will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Total</span>
          <span className={styles.statValue}>{stats.total}</span>
        </div>
        <div className={cn(styles.statItem, styles.pending)}>
          <span className={styles.statLabel}>Pending</span>
          <span className={styles.statValue}>{stats.pending}</span>
        </div>
        <div className={cn(styles.statItem, styles.approved)}>
          <span className={styles.statLabel}>Approved</span>
          <span className={styles.statValue}>{stats.approved}</span>
        </div>
        <div className={cn(styles.statItem, styles.rejected)}>
          <span className={styles.statLabel}>Rejected</span>
          <span className={styles.statValue}>{stats.rejected}</span>
        </div>
      </div>

      {/* Filter */}
      <div className={styles.filterBar}>
        <Dropdown
          options={STATUS_OPTIONS.map(o => ({ key: o.value, value: o.label }))}
          selectedKeys={[statusFilter]}
          onClick={(key) => setStatusFilter(key as FilterStatus)}
        >
          {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'All Statuses'}
        </Dropdown>
        <span className={styles.resultCount}>
          Showing {displayItems.length} of {assets.length}
        </span>
      </div>

      {/* Approvals List */}
      <div className={styles.approvalsList}>
        {displayItems.length === 0 ? (
          <div className={styles.noResults}>
            <p>No assets match the selected filter.</p>
          </div>
        ) : (
          displayItems.map((item) => {
            // Use ODI thumbnail if available, otherwise authenticated URL
            const thumbnailUrl = item.shared_links?.odi
              ? getODIThumbnailUrl({ odi: item.shared_links.odi, width: 80, height: 80 })
              : getThumbnailUrl({
                  width: 80,
                  height: 80,
                  id: item.asset_id,
                  jwt: accessToken,
                });

            return (
              <div key={item.asset_id} className={styles.approvalCard}>
                <div className={styles.assetInfo}>
                  <a
                    href={`/asset/${item.asset_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.thumbnailLink}
                  >
                  <img
                    src={thumbnailUrl}
                    alt="Asset"
                    className={styles.thumbnail}
                  />
                  </a>
                  <div className={styles.assetDetails}>
                    <span className={styles.assetName} title={item.asset_name}>
                      {item.asset_name || `Asset: ${item.asset_id.substring(0, 12)}...`}
                    </span>
                  </div>
                </div>

                <div className={styles.approvalInfo}>
                  <div className={styles.statusBadge}>
                    {getStatusIcon(item.status)}
                    <span className={cn(styles.statusText, getStatusClass(item.status))}>
                      {item.status}
                    </span>
                  </div>

                  {(item.reviewer_name || item.reviewer_email) && (
                    <div className={styles.reviewerInfo}>
                      <span className={styles.reviewerLabel}>Reviewed by:</span>
                      <span className={styles.reviewerName}>
                        {item.reviewer_name || item.reviewer_email}
                      </span>
                    </div>
                  )}

                  {item.notes && (
                    <div className={styles.notes}>
                      <span className={styles.notesLabel}>Notes:</span>
                      <p className={styles.notesText}>{item.notes}</p>
                    </div>
                  )}

                  {item.updated_utc && (
                    <span className={styles.timestamp}>
                      {formatDate(item.updated_utc)}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ShareApprovalsPanel;

